<template>
  <div class="w-screen h-full bg-white">
    <measurement
      v-show="activePage === 'measurement' && !recordToShow"
      :active-page.sync="activePage"
      :has-rmd-sizes="hasRmdSizes"
      :temp-access="tempAccess"
      @show-record="openRecordViewer"
      @menu="showMenu"
      @scan="goScan"
      @ready="onMeasurementViewReady"
    />
    <discover
      v-show="activePage === 'discover'"
      :rmd-sizes="rmdSizes"
      :has-rmd-sizes="hasRmdSizes"
      :active-page.sync="activePage"
      :profile="me"
      @menu="showMenu"
      @edit-profile="startEditing"
    />
    <user-profile
      v-show="activePage === 'profile'"
      :has-rmd-sizes="hasRmdSizes"
      :active-page.sync="activePage"
      :profile="me"
      :temp-access="tempAccess"
      :auth="auth"
      :can-scan="canScan"
      @menu="showMenu"
      @edit-profile="startEditing"
    />
    <edit-profile
      v-show="isEditingProfile"
      :is-editing="isEditingProfile"
      :profile="me"
      :auth="auth"
      @save="saveProfile"
      @cancel="cancelEditProfile"
    />
    <template v-if="recordToShow">
      <feet-viewer
        v-if="isFeetRecord"
        :tid="recordToShow.tid"
        :obj-url="feetObjUrl"
        @back="closeRecordView"
      />
      <body-viewer
        v-else
        :record="recordToShow"
        @show-record="openRecordViewer"
        @back="closeRecordView"
      />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { FEET_KIND } from '@/store/modules/user';
import op from 'object-path';
import ApiActions from '@/modules/api-actions';
import showAppMenu from '@/mixins/show-app-menu';
import page from '@/mixins/page';
import hasSlider from '@/mixins/has-slider2';
import Measurement from '@/components/DefaultCloudzet/HomePage/Measurement.vue';
import BodyViewer from '@/components/DefaultCloudzet/HomePage/BodyViewer.vue';
import UserProfile from '@/components/DefaultCloudzet/HomePage/UserProfile.vue';
import Discover from '@/components/DefaultCloudzet/HomePage/Discover.vue';
import EditProfile from '@/components/DefaultCloudzet/HomePage/EditProfile.vue';
import FeetViewer from '@/components/FootScan/FeetViewer.vue';

export default {
  name: 'HomePage',
  mixins: [page, showAppMenu, hasSlider],
  components: {
    FeetViewer,
    Measurement,
    BodyViewer,
    UserProfile,
    EditProfile,
    Discover,
  },
  data() {
    return {
      activePage: 'measurement',
      recordToShow: false,
      isEditingProfile: false,
      timeoutID: null,
    };
  },
  async created() {
    if (this.appReady || this.canScan) {
      this.activePage = 'measurement';
    }
    await this.emitTokenToParent();

    try {
      await this.getAllKindsOfScanRecords();
    } catch (e) {
      console.log('something went wrong fetching records');
      this.$events.stopLoading();
    }
  },
  beforeDestroy() {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
      this.timeoutID = null;
    }
  },
  methods: {
    ...mapActions('user', ['getAllKindsOfScanRecords', 'getAccessToken', 'getRecommendedSizes']),
    async emitTokenToParent() {
      const token = await this.getAccessToken();
      if (token && window.opener) {
        window.opener.postMessage({ action: 'setToken', data: token }, '*');
      }
    },
    goScan() {
      this.$events.goto('Discovery');
    },
    startEditing() {
      this.isEditingProfile = true;
    },
    openRecordViewer(record) {
      this.recordToShow = record;
    },
    closeRecordView() {
      this.recordToShow = null;
      this.resetSlider();
    },
    async saveProfile(profile) {
      this.$events.startLoading();
      try {
        if (profile.avatar) {
          await ApiActions.user.setAvatar(profile.avatar);
        }
        await ApiActions.user.setProfile(profile);

        // get recommanded size after change gender
        if (this.latestBodyRecord) {
          this.getRecommendedSizes({ tid: this.latestBodyRecord.tid, locale: this.$i18n.locale });
        }

        this.cancelEditProfile();
      } catch (e) {
        console.log(e.message);
        const message = op.get(e, 'response.data.error.msg', '');
        this.$toasted.global.alert(message);
      } finally {
        this.$events.stopLoading();
      }
    },
    cancelEditProfile() {
      this.isEditingProfile = false;
    },
    onMeasurementViewReady() {
      this.$events.stopLoading();
    },
  },
  computed: {
    ...mapState('user', ['auth', 'me', 'isFetchingRecords', 'feetObjUrl']),
    ...mapState('app', ['appReady']),
    ...mapGetters('mtmUser', ['hasFixedScanner']),
    ...mapGetters('user', ['noRecord', 'rmdSizes', 'latestBodyRecord', 'tempAccess']),
    hasRmdSizes() {
      return Object.keys(this.rmdSizes).length > 0;
    },
    canScan() {
      return this.hasFixedScanner;
    },
    isFeetRecord() {
      return this.recordToShow && this.recordToShow.kind === FEET_KIND;
    },
  },
  watch: {
    '$route.query': {
      handler(nv) {
        if (nv && nv.measurement) {
          this.activePage = 'measurement';
        }
      },
    },
    '$i18n.locale': {
      handler(nv) {
        this.getRecommendedSizes({ tid: this.latestBodyRecord.tid, locale: nv });
      },
    },
    activePage() {
      if (this.$route.query.measurement) {
        this.$router.push({ query: null });
      }
    },
    latestBodyRecord(nv) {
      this.getRecommendedSizes({ tid: nv.tid, locale: this.$i18n.locale });
    },
    isFetchingRecords(nv) {
      if (nv) {
        this.$events.startLoading(true);
      } else {
        console.log('done fetching records');
        if (this.noRecord) {
          this.$events.stopLoading();
        } else {
          this.timeoutID = setTimeout(() => {
            this.$events.stopLoading();
            this.timeoutID = null;
          }, 8000);
        }
      }
    },
  },
  mounted() {
    // direct to edit profile
    if (this.$route.query.measurement) {
      this.activePage = 'measurement';
    }
  },
};
</script>
